import React, { memo } from 'react';
import { Card, CardBody, Chip, Avatar } from '@heroui/react';
import { User } from '@/services/apis/users';
import { cn } from '@/utils/cn';
import { Sparkles, User as UserIcon } from 'lucide-react';

interface UserCardProps {
  user: User;
  onClick: () => void;
}

const UserCard: React.FC<UserCardProps> = memo(({ user, onClick }) => {
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User';

  // Generate a color based on user's name for consistent gradient
  const getGradientColor = (name: string) => {
    const colors = [
      'from-indigo-500 to-purple-600',
      'from-blue-500 to-cyan-600',
      'from-purple-500 to-pink-600',
      'from-emerald-500 to-teal-600',
      'from-orange-500 to-red-600',
      'from-violet-500 to-fuchsia-600',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const gradientColor = getGradientColor(fullName);

  return (
    <Card
      isPressable
      onPress={onClick}
      className={cn(
        'group relative overflow-hidden rounded-2xl border border-gray-200/60 dark:border-gray-800/60',
        'bg-white dark:bg-gray-900 shadow-sm hover:shadow-xl transition-all duration-500',
        'flex flex-col h-full backdrop-blur-sm cursor-pointer',
      )}
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-blue-500/5 group-hover:via-purple-500/5 group-hover:to-pink-500/5 transition-all duration-500 pointer-events-none z-10" />
      <CardBody className="p-0">
        {/* Header with modern gradient */}
        <div className="relative overflow-hidden">
          <div className={cn('absolute inset-0 bg-gradient-to-br max-h-[150px]', gradientColor)} />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent" />
          <div className="relative p-6 pb-8">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 text-left min-w-0 pr-2">
                <h3 className="text-2xl font-bold mb-3 line-clamp-2 leading-tight text-white drop-shadow-sm">
                  {fullName}
                </h3>
                {user.position && (
                  <p className="text-white/85 text-sm line-clamp-2 leading-relaxed font-medium">{user.position}</p>
                )}
              </div>
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                  <UserIcon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            {/* Avatar in header */}
            <div className="flex justify-center mb-4">
              <div className="relative">
                {/* Glow effect */}
                <div
                  className={cn(
                    'absolute inset-0 rounded-full blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-500',
                    `bg-gradient-to-br ${gradientColor}`,
                  )}
                />
                <Avatar
                  src={user.avatarFile?.url}
                  name={fullName}
                  size="lg"
                  className="w-24 h-24 text-xl font-bold relative z-10"
                  classNames={{
                    base: cn(
                      'ring-4 ring-white/50 dark:ring-gray-800/50',
                      'group-hover:ring-4 group-hover:ring-white/70',
                      'shadow-xl group-hover:shadow-2xl',
                      'transition-all duration-500',
                      'group-hover:scale-110',
                    ),
                  }}
                />
                {/* Decorative sparkle icon */}
                <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className={cn('p-1.5 rounded-full bg-gradient-to-br shadow-lg', gradientColor)}>
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Department Badge with modern styling */}
            {user.department && (
              <div className="flex flex-wrap gap-2">
                <Chip size="md" color="warning" variant="shadow">
                  {user.department.name}
                </Chip>
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
});

UserCard.displayName = 'UserCard';

export default UserCard;
